import { useEffect, type JSX } from 'react';
import { createPortal } from 'react-dom';
import type { Notification } from '../../engine/api';
import { Icon } from '../components/Icon';
import { useRuntime, useStudio, useUi, useUiContext } from '../context';
import { safely } from '../safe';
import '../health/health.css';
import './shell.css';

/** Errors stay until dismissed; lighter messages leave by themselves. */
export const AUTO_DISMISS_MS: Record<Notification['level'], number | null> = { info: 5000, warn: 9000, error: null };
const MAX_VISIBLE = 5;

/**
 * Runs `onElapsed` after `delayMs` of the page being visible. A message that
 * arrives (or is still showing) while the tab is hidden waits for the operator
 * to come back and then gets its full reading time.
 */
export function dismissWhenSeen(delayMs: number, onElapsed: () => void, doc: Document = document): () => void {
  let handle: ReturnType<typeof setTimeout> | undefined;
  const sync = (): void => {
    clearTimeout(handle);
    handle = doc.visibilityState === 'hidden' ? undefined : setTimeout(onElapsed, delayMs);
  };
  sync();
  doc.addEventListener('visibilitychange', sync);
  return () => {
    clearTimeout(handle);
    doc.removeEventListener('visibilitychange', sync);
  };
}

/** The newest messages that fit, never letting a newer note push an undismissed error off screen. */
export function visibleNotifications(all: readonly Notification[], max = MAX_VISIBLE): Notification[] {
  const errors = all.filter((n) => n.level === 'error').slice(-max);
  const others = all.filter((n) => n.level !== 'error').slice(-Math.max(0, max - errors.length));
  const keep = new Set([...errors, ...others]);
  return all.filter((n) => keep.has(n));
}

function Toast({ notification }: { notification: Notification }): JSX.Element {
  const studio = useStudio();
  const { id, level } = notification;
  useEffect(() => {
    const delay = AUTO_DISMISS_MS[level];
    if (delay === null) return;
    return dismissWhenSeen(delay, () => studio.dismissNotification(id));
  }, [studio, id, level]);
  return (
    <div className={`toast ${level}`} role={level === 'error' ? 'alert' : 'status'}>
      <span className="toast-message">{notification.message}</span>
      <button type="button" className="ui-icon-btn" aria-label="Dismiss notification" onClick={() => studio.dismissNotification(id)}>
        <Icon name="close" size={14} />
      </button>
    </div>
  );
}

/** Offer to turn on system notifications, shown once per session when the user first goes live. */
function AlertOffer(): JSX.Element | null {
  const { studio, ui } = useUiContext();
  const state = useUi((s) => s.alertOffer);
  if (state !== 'open') return null;
  const close = (): void => ui.getState().setAlertOffer('done');
  return (
    <div className="alert-offer" role="region" aria-label="System notifications">
      <span>Get a system notification if the stream runs into trouble while this tab is in the background?</span>
      <div className="ui-row">
        <button
          type="button"
          className="ui-btn primary"
          onClick={() => {
            close();
            void safely(studio, async () => {
              const result = await studio.requestAlertPermission();
              if (result === 'granted') studio.notify('info', 'System notifications are on for stream problems.');
              else if (result === 'denied') studio.notify('warn', 'The browser blocked notifications for this site; allow them in the site settings to be alerted.');
              else if (result === 'unsupported') studio.notify('warn', 'System notifications are not available here; stream problems still show in the studio.');
            });
          }}
        >
          Enable notifications
        </button>
        <button type="button" className="ui-btn" onClick={close}>
          Not now
        </button>
        <button
          type="button"
          className="ui-btn"
          onClick={() => {
            ui.getState().setPrefs({ offerAlerts: false });
            close();
          }}
        >
          Don't ask again
        </button>
      </div>
    </div>
  );
}

export function Toasts(): JSX.Element {
  const notifications = useRuntime((r) => r.notifications);
  const visible = visibleNotifications(notifications);
  return createPortal(
    <div className="toasts" aria-label="Notifications">
      <AlertOffer />
      {visible.map((notification) => (
        <Toast key={notification.id} notification={notification} />
      ))}
    </div>,
    document.body,
  );
}
